// frontend/src/pages/checkout/SuccessPage.tsx
//
// 支付成功页（路由 /checkout/success）。
// 数据流不变：GET /checkout/:id/status。

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import api from '../../services/api';
import CheckoutShell from './CheckoutShell';
import { notifyCheckoutResult, isEmbedded } from '../../utils/checkoutBridge';
import './checkout.css';

const formatMoney = (amount: number, currency: string = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format((amount || 0) / 100);
  } catch {
    return `${((amount || 0) / 100).toFixed(2)} ${currency || 'USD'}`;
  }
};

const SuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [paymentIntent, setPaymentIntent] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const paymentIntentId = searchParams.get('payment_intent');
  const embedded = isEmbedded();

  useEffect(() => {
    if (paymentIntentId) {
      loadPaymentIntent();
    } else {
      setLoading(false);
    }
    // 进入成功页即通知宿主（embedded / Flutter 场景）。普通跳转模式无 redirectUrl，
    // 不会触发跳转，仍正常渲染本页。
    notifyCheckoutResult('success', {
      payment_intent_id: paymentIntentId || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentIntentId]);

  const loadPaymentIntent = async () => {
    try {
      const response = await api.get(`/checkout/${paymentIntentId}/status`);
      if (response.data.success) {
        setPaymentIntent(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load payment intent', error);
    } finally {
      setLoading(false);
    }
  };

  const successUrl: string | undefined =
    paymentIntent?.success_url || paymentIntent?.success_redirect_url;
  const merchantName: string | undefined =
    paymentIntent?.merchant_name || paymentIntent?.display_name;
  const logoUrl: string | undefined = paymentIntent?.logo_url;

  const handleBack = () => {
    if (successUrl) {
      const url = new URL(successUrl, window.location.origin);
      url.searchParams.set('faststar_status', 'success');
      if (paymentIntent?.id) url.searchParams.set('payment_intent_id', paymentIntent.id);
      window.location.href = url.toString();
    } else {
      window.close();
    }
  };

  if (loading) {
    return (
      <CheckoutShell hideBrand={embedded}>
        <div className="fs-card fs-card--single">
          <div className="fs-result">
            <div className="fs-skeleton" style={{ width: 72, height: 72, borderRadius: '50%', margin: '0 auto 20px' }} />
            <div className="fs-skeleton" style={{ width: 160, height: 22, margin: '0 auto 12px' }} />
            <div className="fs-skeleton" style={{ width: 220, height: 14, margin: '0 auto 28px' }} />
            <div className="fs-skeleton" style={{ width: '100%', height: 120 }} />
          </div>
        </div>
      </CheckoutShell>
    );
  }

  return (
    <CheckoutShell merchantName={merchantName} logoUrl={logoUrl} hideBrand={embedded}>
      <div className="fs-card fs-card--single">
        <div className="fs-result">
          <div className="fs-result-icon fs-result-icon--success">
            <CheckOutlined />
          </div>
          <div className="fs-result-title">支付成功!</div>
          <div className="fs-result-subtitle">
            您的支付已成功处理，感谢您的购买。
          </div>

          {paymentIntent && (
            <div className="fs-result-meta">
              <div className="fs-result-meta-row">
                <span className="fs-result-meta-key">订单号</span>
                <span className="fs-result-meta-val">{paymentIntent.id}</span>
              </div>
              <div className="fs-result-meta-row">
                <span className="fs-result-meta-key">支付金额</span>
                <span className="fs-result-meta-val">
                  {formatMoney(paymentIntent.amount, paymentIntent.currency)}
                </span>
              </div>
              {paymentIntent.fee > 0 && (
                <div className="fs-result-meta-row">
                  <span className="fs-result-meta-key">手续费</span>
                  <span className="fs-result-meta-val">
                    {formatMoney(paymentIntent.fee, paymentIntent.currency)}
                  </span>
                </div>
              )}
              <div className="fs-result-meta-row">
                <span className="fs-result-meta-key">支付状态</span>
                <span className="fs-result-meta-val">
                  {paymentIntent.status === 'succeeded' ? '已完成' : paymentIntent.status}
                </span>
              </div>
              {paymentIntent.description && (
                <div className="fs-result-meta-row">
                  <span className="fs-result-meta-key">描述</span>
                  <span className="fs-result-meta-val">{paymentIntent.description}</span>
                </div>
              )}
            </div>
          )}

          <div className="fs-result-actions">
            <Button type="primary" size="large" block onClick={handleBack}>
              返回商家
            </Button>
          </div>
        </div>
      </div>
    </CheckoutShell>
  );
};

export default SuccessPage;
